/**
 * Stripe billing mode: production hostnames always use live keys;
 * development + staging default to test (sandbox) with independent Firestore toggles.
 *
 * Firestore: site_config/stripe_settings
 *   useLiveInDevelopment (boolean, default false)
 *   useLiveInStaging     (boolean, default false)
 */

/** @typedef {'development' | 'staging' | 'production'} StripeUiTier */

/**
 * @param {string} hostname window.location.hostname (lowercase ok)
 * @returns {StripeUiTier}
 */
export function stripeUiTierFromHostname(hostname) {
  if (!hostname || typeof hostname !== 'string') return 'development';
  const h = hostname.toLowerCase();
  if (h.includes('localhost') || h.includes('127.0.0.1')) return 'development';
  if (h.includes('staging')) return 'staging';
  return 'production';
}

/**
 * @param {StripeUiTier} tier
 * @param {{ useLiveInDevelopment?: boolean, useLiveInStaging?: boolean }} settings
 * @returns {'live' | 'test'}
 */
export function effectiveStripeBillingMode(tier, settings = {}) {
  if (tier === 'production') return 'live';
  if (tier === 'staging') return settings.useLiveInStaging === true ? 'live' : 'test';
  return settings.useLiveInDevelopment === true ? 'live' : 'test';
}

/**
 * @param {'live' | 'test'} mode
 */
export function billingModeDisplay(mode) {
  if (mode === 'live') {
    return { label: 'Live', shortLabel: 'Live', color: '#10B981' };
  }
  return { label: 'Test (sandbox)', shortLabel: 'Test', color: '#F59E0B' };
}

function trimKey(v) {
  return typeof v === 'string' ? v.trim() : '';
}

/**
 * Pick publishable key for Stripe.js. Prefer explicit test/live env keys; fall back to legacy single key if prefix matches mode.
 * @param {Record<string, string>} extra Constants.expoConfig.extra
 * @param {'live' | 'test'} mode
 */
export function selectPublishableKey(extra, mode) {
  const e = extra || {};
  const test = trimKey(e.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY_TEST || e.STRIPE_PUBLISHABLE_KEY_TEST);
  const live = trimKey(e.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY_LIVE || e.STRIPE_PUBLISHABLE_KEY_LIVE);
  const legacy = trimKey(e.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY || e.STRIPE_PUBLISHABLE_KEY);

  if (mode === 'live') {
    if (live) return live;
    if (legacy.startsWith('pk_live_')) return legacy;
    return live;
  }
  if (test) return test;
  if (legacy.startsWith('pk_test_')) return legacy;
  return test;
}
